/* eslint-disable react/no-multi-comp */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Select, { components as selectComponents } from 'react-select-2';
import BaseFilter from './BaseFilter';
import Button from '../Button';
import AsyncCheckboxList from '../Form/AsyncCheckboxList';
import MaterialCheckbox from '../../material/MaterialCheckbox';
import Wizard from '../Wizard';
import Step from '../Step';
import BackButton from '../BackButton';
import Icon from '../Icon';
import noop from '../../util/noop';
import { onExecuteKeyDown } from '../../util/KeyboardUtil';
import i18n from '../../i18n/translate';
import uniqBy from 'lodash/uniqBy';
import { pickDataAttributes } from '../../Util';
import { kebabCase } from 'lodash';

const MAX_OPTIONS = 10;
const ClearIndicator = null;
const IndicatorSeparator = null;

const SelectFilter = props => {
  const {
    asyncOptions = {}, customComponent, defaultValue,
    explainerKey,
    iconFamily,
    iconKey,
    isAsync,
    isTwoStep, labelKey, onChange = noop, onClearFilters = noop, onMenuClose = noop, onMenuOpen = noop,
    options = [],
    selectedKey,
    showHelperText,
    showSelected,
    valueKey,
    ...selectProps
  } = props;

  const [value, setValue] = useState(defaultValue);
  const [isOpen, setIsOpen] = useState(false);
  const [wizardStep, setWizardStep] = useState(0);

  //this is the ID of the category that's currently being shown, if any
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);

  //this array holds a copy of the category object with the selected values for it
  //only categories that have options selected are included
  const [selectedCategories, setSelectedCategories] = useState([]);

  const [asyncData, setAsyncData] = useState([]);
  const isSelectingCategory = isTwoStep && wizardStep === 0;

  useEffect(() => {
    if (selectProps.value) {
      if (isTwoStep) {
        // copy selected category values from props to state
        const newCategories = options.reduce((acc, option) => {
          const valueCategory = selectProps.value.find(val => val.id === option.id);
          if (valueCategory) {
            const values = [ ...valueCategory.values ];
            acc.push({ ...valueCategory, values });
          }
          return acc;
        }, []);
        setSelectedCategories(newCategories);
      } else {
        setValue(selectProps.value);
      }
    }
  }, [selectProps.value]);

  useEffect(() => {
    if (defaultValue) {
      if (isTwoStep) {
        // copy default category values from props to state
        // lookup and add any selected values in the category to the copied object
        const selectedCategories = defaultValue.map(cat => {
          const category = options.find(opt => opt.id === cat.id);
          if (category) {
            const values = cat.values.map(val => category.categoryOptions.find(opt => opt.id === val.id));
            return { ...category, values };
          }
        }).filter(cat => cat);
        if(selectedCategories.length) {
          setSelectedCategories(selectedCategories);
          onChange(selectedCategories);
        }
      } else {
        setValue(defaultValue);
      }
    }
  }, [defaultValue]);

  const performSetValue = val => {
    setValue(val);
    onChange(val);
  };

  const performSetCategoryValues = newValues => {
    let selectedCategory = getSelectedCategory();
    const newCategories = selectedCategories.filter(cat => cat.id !== selectedCategoryId);
    if (newValues.length > 0) {
      newCategories.push({ ...selectedCategory, values: newValues });
    }
    setSelectedCategories(newCategories);
    onChange(newCategories);
  }

  const performTwoStepSetValue = (option, action) => {
    if (isSelectingCategory) {
      if (action === 'go-forward'){
        if (selectedCategoryId !== option.id) {
          setSelectedCategoryId(option.id);
        }
        setWizardStep(1);
      } else if (action === 'clear') {
        setSelectedCategories([]);
        onChange([]);
      }
    } else {
      let newValues;
      let selectedCategory = getSelectedCategory();
      const currentValues = selectedCategory.values || [];
      switch (action) {
      case 'deselect-option' :
        newValues = currentValues.filter(val => val.id !== option.id);
        break;
      case 'select-option' :
        if (!currentValues.includes(val => val.id === option.id)) {
          newValues = [ ...currentValues, option ]
        } else {
          newValues = [ ...currentValues ];
        }
        break;
      case 'clear':
        setWizardStep(0);
        newValues = [];
        break;
      }
      performSetCategoryValues(newValues);
    }
  }

  const onSelectChange = (val, action) => {
    if (customComponent) {
      let toggleVal = val.id ? false : { id: true, label: selectProps.checkedLabel || `${i18n.t('Filtering by')} ${selectProps.label}` }
      performSetValue(toggleVal);
      setIsOpen(true);
      return;
    }

    if (!selectProps.isMulti && selectProps.value === val) {
      performSetValue({});
      setIsOpen(false);
      return;
    }

    if (isTwoStep) {
      performTwoStepSetValue(action.option, action.action);
    } else {
      switch (action.action) {
      case 'select-option':
        performSetValue(val);
        break;
      case 'deselect-option':
        performSetValue(!value || value.length === 1 ? undefined : value.filter(value => value[valueKey] !== action.option[valueKey]));
        break;
      case 'clear':
        performSetValue(undefined);
        break;
      }
      if (!selectProps.isMulti) {
        setIsOpen(false);
      }
    }
  };

  const findInAsyncData = keyField => {
    const selectValue = selectProps.value || value;
    const item = asyncData.find(item => String(item[valueKey]) === keyField);

    // if it's not in the asyncData yet, it means it comes pre-selected
    return item || selectValue.find(item => String(item[valueKey]) === keyField);
  }

  const onAsyncSelectChange = (values) => {
    const newValues = values.map(value => findInAsyncData(value));
    if (isTwoStep) {
      performSetCategoryValues(newValues);
    } else {
      performSetValue(newValues);
    }
  };

  const onToggleOpen = isOpen => {
    setIsOpen(isOpen);
    if (isOpen) {
      onMenuOpen();
    } else {
      onMenuClose();
    }
  };


  const IndicatorsContainer = () => (
    <div className="filter-search-indicator">
      <Icon iconType="search" />
    </div>
  );

  const renderClearFilters = (setValue = noop) => {
    const onClear = () => {
      onClearFilters();
      setValue(undefined, "clear");
    }

    return (
      <div className="filter-clear">
        <Button
          onClick={onClear}
          onKeyDown={onExecuteKeyDown(onClear)}
          style="link"
          icon="close"
          label={i18n.t('Clear Filter')}
        />
      </div>
    );
  };

  const Menu = props => (
    <selectComponents.Menu {...props}>
      {props.children}
      {props.hasValue && selectProps.clearable && renderClearFilters(props.setValue)}
    </selectComponents.Menu>
  );

  Menu.propTypes = {
    hasValue: PropTypes.bool,
    setValue: PropTypes.func
  };

  const renderOptionLabel = data => {
    return (
      <>
        {iconKey && <Icon iconType={data[iconKey]} fontFamily={iconFamily} />}
        <div
          className="option-container"
          data-object-type="menu-dropdown-options"
          data-action={`select-option-${kebabCase(data.label)}`}
          data-object-id={data.name}
        >
          <div className="option-label">{data[labelKey]}</div>
          {explainerKey && <div className="option-explainer">{data[explainerKey]}</div>}
        </div>
      </>
    )};

  const isSearchable = !!options && options.length > MAX_OPTIONS;
  const Control = isSearchable ? selectComponents.Control : () => <div tabIndex={0} />;
  const selectionVisible = !selectProps.isMulti && showSelected && !!value;

  const Option = props => {
    const dataAttributes = pickDataAttributes(props, 'select-option');

    if(!dataAttributes['data-action']){
      dataAttributes['data-action'] = props.label ? `select-${kebabCase(props.label)}-option` : 'select-option';
    }

    if (customComponent) {
      let CustomOptionComponent = customComponent.Option;
      return <CustomOptionComponent {...props} />;
    }

    if (isTwoStep && isSelectingCategory) {
      const { children, data: { id } } = props;
      const selectedValues = selectedCategoryValues(id).length;
      return (
        <selectComponents.Option {...props} {...dataAttributes}>
          <div className="option-text">
            {children}
          </div>
          <div className="option-right">
            {selectedValues > 0 &&
            <span className="option-count">
              {selectedCategoryValues(id).length}
            </span>}
            <Icon iconType={'chevron-right'} />
          </div>
        </selectComponents.Option>
      );
    }

    if (!showHelperText) {
      return <selectComponents.Option {...props} {...dataAttributes}/>;
    }

    const { children, data: { helperText } } = props;
    return (
      <selectComponents.Option {...props} {...dataAttributes}>
        <div className="option-text">
          {children}
        </div>
        {!!helperText && (
          <span className="helper-text">
            {helperText}
          </span>
        )}
      </selectComponents.Option>
    );
  };

  const getSelectedCategory = (id = selectedCategoryId) => {
    let selectedCategory = selectedCategories.find(cat => cat.id === id);
    if (!selectedCategory) {
      selectedCategory = options.find(opt => opt.id === id);
    }
    return selectedCategory;
  }

  const selectedCategoryValues = (id = selectedCategoryId) => {
    const selectedCategory = getSelectedCategory(id);
    return (selectedCategory && selectedCategory.values) || [];
  }
  const selectedCategoryOptions = (id = selectedCategoryId) => {
    const selectedCategory = getSelectedCategory(id);
    return (selectedCategory && selectedCategory.categoryOptions) || [];
  }
  const selectedCategoryAsyncOptions = (id = selectedCategoryId) => {
    const selectedCategory = getSelectedCategory(id);
    return (selectedCategory && selectedCategory.asyncOptions) || [];
  }

  const renderSelect = (options = [], isMulti = false, isAsync = false, asyncOptions = {}) => {
    const isOptionSelected = option => {
      if (isTwoStep && isSelectingCategory) {
        return selectedCategoryValues(option.id).length > 0;
      }
      return Array.isArray(value) ? value.some((elem) => {
        const item = elem[valueKey]; 
        if (item) {
          return item === option[valueKey];
        }
        return elem === option[valueKey];
      }) : value === option[valueKey];
    };

    const onChange = (val, action) => {
      if (!isTwoStep || !isSelectingCategory || action.action === 'clear') {
        onSelectChange(val, action);
      } else {
        onSelectChange(action.option, { action: 'go-forward', option: action.option });
      }
    };

    const selectValue = selectProps.value || value;
    const currentOptions = [ ...options ];

    return isAsync ? (
      <AsyncCheckboxList
        {...selectProps}
        {...asyncOptions}
        CheckboxComponent={MaterialCheckbox}
        key={selectedCategoryId}
        labelKey={labelKey}
        onChange={onAsyncSelectChange}
        pageSize={asyncOptions.pageSize || MAX_OPTIONS}
        renderClearFilters={props.clearable ? renderClearFilters : null}
        searchWrapperClasses="filter-search"
        selectedValues={selectValue ? selectValue.map(val => val[valueKey]) : []}
        showSelectOptions={false}
        valueKey={valueKey}
        onDataLoaded={data => setAsyncData(uniqBy([...asyncData, ...data], valueKey))}
      />
    ) : (
      <Select
        {...selectProps}
        autoFocus={true}
        backspaceRemovesValue={false}
        className="filter-select"
        classNamePrefix="filter-select"
        components={{
          ClearIndicator,
          Control,
          IndicatorsContainer,
          IndicatorSeparator,
          Menu,
          Option
        }}
        controlShouldRenderValue={false}
        formatOptionLabel={renderOptionLabel}
        hideSelectedOptions={false}
        isClearable={props.clearable}
        isMulti={isMulti}
        isSearchable={isSearchable}
        isOptionSelected={isOptionSelected}
        key={selectedCategoryId}
        menuIsOpen={true}
        onChange={onChange}
        options={currentOptions}
        placeholder={i18n.t('Search')}
        tabSelectsValue={false}
        value={isTwoStep ? selectedCategoryValues() : selectValue}
      />
    );
  }

  let wizardSteps;
  if (isTwoStep) {
    wizardSteps = [
      // First step: category selection
      <Step>
        <div className="step-1">
          {renderSelect(options, true)}
        </div>
      </Step>,
      // Second step: value selection
      <Step>
        {renderSelect(
          selectedCategoryOptions(),
          true,
          isAsync,
          selectedCategoryAsyncOptions()
        )}
      </Step>
    ];
  }

  return (
    <BaseFilter
      {...props}
      isOpen={isOpen}
      onToggleOpen={onToggleOpen}
      selected={selectionVisible && value[selectedKey || labelKey]}
      selectedIcon={selectionVisible && iconKey && value[iconKey]}
      selectedIconFamily={iconFamily}
    >
      {isTwoStep ? (
        <div className="two-step">
          <h5 className="category-title">
            {!isSelectingCategory ? (
              <>
                <BackButton action={() => {
                  setSelectedCategoryId(null);
                  setWizardStep(0);
                }} />
                {getSelectedCategory().label}
              </>
            ) : i18n.t('Select a Category')}
          </h5>
          <Wizard
            children={wizardSteps}
            wizardStep={wizardStep}
            initialStep={0}
            onCancel={noop}
            onComplete={noop}
            hasStepIndicator={false}
            hasButtons={false}
            overrideSteps={true}
          />
        </div>
      ) : renderSelect(options, selectProps.isMulti, isAsync, asyncOptions)}
    </BaseFilter>
  );
}

SelectFilter.propTypes = {
  asyncOptions: PropTypes.shape({
    dataUrl: PropTypes.string,
    queryFields: PropTypes.object,
    pageSize: PropTypes.number
  }),
  children: PropTypes.node,
  clearable: PropTypes.bool,
  customComponent: PropTypes.object,
  data: PropTypes.object,
  defaultValue: PropTypes.any,
  disabled: PropTypes.bool,
  explainerKey: PropTypes.string,
  iconFamily: PropTypes.string,
  iconKey: PropTypes.string,
  isAsync: PropTypes.bool,
  isMulti: PropTypes.bool,
  isTwoStep: PropTypes.bool,
  labelKey: PropTypes.string,
  label: PropTypes.string,
  onChange: PropTypes.func,
  onClearFilters: PropTypes.func,
  onMenuClose: PropTypes.func,
  onMenuOpen: PropTypes.func,
  options: PropTypes.array,
  selected: PropTypes.string,
  selectedKey: PropTypes.string,
  showHelperText: PropTypes.bool,
  showSelected: PropTypes.bool,
  valueKey: PropTypes.string
}

SelectFilter.defaultProps = {
  clearable: true,
  labelKey: 'label',
  valueKey: 'id'
}

export default SelectFilter;
